import { useState } from "react";
import moment from "moment-timezone";
import Decimal from "decimal.js";
import type { UseQueryResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, SlidersHorizontal } from "lucide-react";
import {
  type AgtechSettlementsFilters,
  type AgtechSettlementsResponse,
  type SettlementOrAdvanceItem,
  type SettlementContractInfo,
  type AdvanceContractInfo,
} from "@/lib/api/agtech/ticketsExpanded";
import type { PossibleReturnType } from "@/lib/responseResults";
import { useListAgtechCommodities, type AgtechCommodity } from "@/lib/api/agtech/lookup";
import { SettlementOrAdvanceFilterModal } from "./SettlementOrAdvanceFilterModal";
import { ContractTicketsModal } from "../agtechTickets/ContractTicketsModal";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SETTLEMENT_OR_ADVANCE_FILTER_VALUES,
  type SettlementOrAdvanceApiFilterValues,
} from "./types";

// ── Helpers ────────────────────────────────────────────────────────

function fmtDollar(val: string | null | undefined): string {
  if (val === null || val === undefined) return "—";
  return `$${new Decimal(val).toNumber().toLocaleString()}`;
}

function fmtNum(val: string | null | undefined): string {
  if (val === null || val === undefined) return "—";
  return new Decimal(val).toNumber().toLocaleString();
}

// ── Contract Summary Table ─────────────────────────────────────────

type ContractSummaryRow = SettlementContractInfo | AdvanceContractInfo;

function ContractSummaryTable({ rows }: { rows: ContractSummaryRow[] }) {
  if (rows.length === 0) return null;

  const totalPriced = rows.reduce((acc, r) => acc.plus(new Decimal(r.balance_forward ?? 0)), new Decimal(0));
  const totalSettled = rows.reduce((acc, r) => acc.plus(new Decimal(r.applied ?? 0)), new Decimal(0));
  const totalGross = rows.reduce((acc, r) => acc.plus(new Decimal(r.gross_dollars ?? 0)), new Decimal(0));
  const totalDiscounts = rows.reduce((acc, r) => acc.plus(new Decimal(r.discounts ?? 0)), new Decimal(0));
  const totalCharges = rows.reduce((acc, r) => acc.plus(new Decimal(r.charges ?? 0)), new Decimal(0));
  const totalSettlementAmount = totalGross.minus(totalDiscounts).minus(totalCharges);

  return (
    <div className="rounded-lg border bg-muted/50 p-3 space-y-2">
      <span className="text-xs font-semibold">Contract Summary</span>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-[10px] font-medium text-muted-foreground">
              <th className="text-left py-1 pr-3">Contract #</th>
              <th className="text-left py-1">Contract Type</th>
              <th className="text-left py-1">Contract Date</th>
              <th className="text-right py-1 pr-3">Price</th>
              <th className="text-right py-1 pr-3">Priced</th>
              <th className="text-right py-1 pr-3">Settled</th>
              <th className="text-right py-1 pr-3">Gross $</th>
              <th className="text-right py-1 pr-3">Discounts</th>
              <th className="text-right py-1 pr-3">Charges</th>
              <th className="text-right py-1">Settlement Amt</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="py-1 pr-3">{r.contract_id}</td>
                <td className="py-1">{r.pricing_type_code}</td>
                <td className="py-1">{r.contract_date ? moment.tz(r.contract_date, "UTC").format("MM/DD/YYYY") : ""}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.price)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.balance_forward)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.applied)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.gross_dollars)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.discounts)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.charges)}</td>
                <td className="text-right py-1 font-semibold">
                  {fmtDollar(new Decimal(r.gross_dollars ?? 0).minus(new Decimal(r.discounts ?? 0)).minus(new Decimal(r.charges ?? 0)).toString())}
                </td>
              </tr>
            ))}
            <tr className="border-t border-border font-semibold">
              <td className="py-1.5 pr-3">Total</td>
              <td className="py-1.5"></td>
              <td className="py-1.5"></td>
              <td className="py-1.5"></td>
              <td className="text-right py-1.5 pr-3">{totalPriced.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalSettled.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalGross.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalDiscounts.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalCharges.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5">${totalSettlementAmount.toNumber().toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Settlement/Advance Card ────────────────────────────────────────

function SettlementOrAdvanceCard({
  item,
  isExpanded,
  onToggle,
  onCardClick,
  commodityOptions,
}: {
  item: SettlementOrAdvanceItem;
  isExpanded: boolean;
  onToggle: () => void;
  onCardClick: (e: React.MouseEvent) => void;
  commodityOptions: { value: string; label: string }[];
}) {
  const isSettlement = item.type === "settlement";

  const serialNbr = isSettlement ? item.settlement.serial_nbr : item.advance.serial_nbr;
  const date = isSettlement ? item.settlement.settlement_date : item.advance.advance_date;
  const amount = isSettlement ? item.settlement.settlement_amount : item.advance.advance_amount;
  const customer = isSettlement ? item.settlement.Customer : item.advance.Customer;
  const vendorId = isSettlement ? item.settlement.vendor_id : item.advance.vendor_id;
  const commodityId = isSettlement ? item.settlement.commodity_id : item.advance.commodity_id;
  const contractSummaryInfo: ContractSummaryRow[] = isSettlement
    ? item.contractSummaryInfo
    : item.contractSummaryInfo;

  const customerDisplay = customer?.long_name ?? vendorId;
  const commodityDisplay = commodityOptions.find((c) => c.value === commodityId)?.label ?? commodityId;

  const contractCount = contractSummaryInfo.length;

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      {/* Collapsed header — always visible */}
      <div
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-muted/50 transition-colors rounded-lg"
      >
        {/* Chevron */}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}

        {/* Type badge */}
        <Badge
          variant={isSettlement ? "default" : "secondary"}
          className="text-[10px] px-1.5 py-0"
        >
          {isSettlement ? "Settlement" : "Advance"}
        </Badge>

        {/* Serial # */}
        <span className="text-sm font-semibold whitespace-nowrap">
          #{serialNbr ?? "—"}
        </span>

        {/* Summary fields */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 flex-1 min-w-0">
          {date && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {moment.tz(date, "UTC").format("MM/DD/YYYY")}
            </span>
          )}
          {amount && (
            <span className="text-xs font-medium whitespace-nowrap">
              {fmtDollar(amount)}
            </span>
          )}
          {customerDisplay && (
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
              {customerDisplay}
            </span>
          )}
          {commodityDisplay && (
            <span className="text-xs text-muted-foreground truncate max-w-[150px]">
              {commodityDisplay}
            </span>
          )}
        </div>

        {/* Contract count badge */}
        {contractCount > 0 && (
          <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
            {contractCount} contract{contractCount === 1 ? "" : "s"}
          </span>
        )}

        <Button variant="outline" size="sm" onClick={onCardClick}>
            View Tickets
        </Button>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Contract Summary Table */}
          {contractSummaryInfo.length > 0 && (
            <ContractSummaryTable rows={contractSummaryInfo} />
          )}

          {/* View Details Button */}
          <div className="flex justify-end">

          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────

interface SettlementsOrAdvancesPageProps {
  type: "settlements" | "advances";
  useListHook: (filters: AgtechSettlementsFilters) => UseQueryResult<PossibleReturnType<AgtechSettlementsResponse>>;
}

export function SettlementsOrAdvancesPage({ type, useListHook }: SettlementsOrAdvancesPageProps) {
  const isSettlements = type === "settlements";

  // ── Applied filter state (drives the API) ────────
  const [appliedFilters, setAppliedFilters] = useState<SettlementOrAdvanceApiFilterValues>(DEFAULT_SETTLEMENT_OR_ADVANCE_FILTER_VALUES);

  // ── Filter modal state ───────────────────────────
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<SettlementOrAdvanceApiFilterValues>(DEFAULT_SETTLEMENT_OR_ADVANCE_FILTER_VALUES);

  // ── Pagination state ─────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // ── Card expand state ────────────────────────────
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // ── Contract modal state ─────────────────────────
  const [selectedInfo, setSelectedInfo] = useState<{
    settlementNbr?: number;
    advanceNbr?: number;
  } | null>(null);

  // ── Data for comboboxes ──────────────────────────
  const commoditiesResult = useListAgtechCommodities();

  const commodities: AgtechCommodity[] = commoditiesResult?.data?.data || [];
  const commodityOptions = commodities.map((c) => ({
    value: c.commodity_id,
    label: c.name,
  }));

  // ── Build filters object ─────────────────────────
  const filters: AgtechSettlementsFilters = {
    page,
    page_size: pageSize,
    ...(appliedFilters.commodityId ? { commodity_id: appliedFilters.commodityId } : {}),
    ...(isSettlements && appliedFilters.serialSettlementNbrs ? { serial_settlement_nbrs: appliedFilters.serialSettlementNbrs } : {}),
    ...(!isSettlements && appliedFilters.serialAdvanceNbrs ? { serial_advance_nbrs: appliedFilters.serialAdvanceNbrs } : {}),
  };

  // ── Data fetching ────────────────────────────────
  const listResult = useListHook(filters);

  const isSuccess = !!listResult?.data?.success;
  const items: SettlementOrAdvanceItem[] = isSuccess && listResult.data ? listResult.data.data.data : [];
  const pagination = isSuccess && listResult.data ? listResult.data.data.pagination : null;
  const totalPages = pagination?.total_pages ?? 1;
  const totalCount = pagination?.total_count ?? 0;

  // ── Filter modal handlers ────────────────────────
  const handleOpenFilterModal = () => {
    setDraftFilters({ ...appliedFilters });
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
    setIsFilterModalOpen(false);
  };

  // ── Card toggle handler ──────────────────────────
  const getItemKey = (item: SettlementOrAdvanceItem): string => {
    if (item.type === "settlement") {
      return `settlement-${item.settlement.settlement_nbr}`;
    }
    return `advance-${item.advance.advance_nbr}`;
  };

  const handleToggle = (key: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCardClick = (item: SettlementOrAdvanceItem) => {
    if (item.type === "settlement") {
      setSelectedInfo({ settlementNbr: item.settlement.settlement_nbr });
    } else {
      setSelectedInfo({ advanceNbr: item.advance.advance_nbr });
    }
  };

  const hasActiveFilters =
    appliedFilters.commodityId !== "" ||
    (isSettlements && appliedFilters.serialSettlementNbrs !== "") ||
    (!isSettlements && appliedFilters.serialAdvanceNbrs !== "");

  const emptyMessage = isSettlements ? "No settlements found." : "No advances found.";

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      {/* Filter Button */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleOpenFilterModal}>
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-xs">
              Active
            </span>
          )}
        </Button>
        {listResult.isFetching && (
          <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
        )}
      </div>

      {/* Filter Modal */}
      <SettlementOrAdvanceFilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        draftFilters={draftFilters}
        setDraftFilters={setDraftFilters}
        onApply={handleApplyFilters}
        commodityOptions={commodityOptions}
        type={type}
      />

      {/* Contract Tickets Modal */}
      <ContractTicketsModal
        contractNbr={null}
        contractId={null}
        settlementNbr={selectedInfo?.settlementNbr ?? null}
        advanceNbr={selectedInfo?.advanceNbr ?? null}
        onClose={() => setSelectedInfo(null)}
      />

      {/* Card List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {listResult.isLoading && (
          <div className="text-sm text-muted-foreground text-center py-8">Loading...</div>
        )}
        {!listResult.isLoading && items.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-8">{emptyMessage}</div>
        )}
        {!listResult.isLoading &&
          items.map((item) => {
            const key = getItemKey(item);
            return (
              <SettlementOrAdvanceCard
                key={key}
                item={item}
                isExpanded={expandedIds.has(key)}
                onToggle={() => handleToggle(key)}
                onCardClick={(e) => {
                  e.stopPropagation();
                  handleCardClick(item);
                }}
                commodityOptions={commodityOptions}
              />
            );
          })}
      </div>

      {/* Pagination Controls */}
      {!listResult.isLoading && (
        <div className="flex items-center justify-between py-2">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages} | Total: {totalCount} items
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
